import { Loader2, Send, MapPin } from "lucide-react";
import { THC } from "@/lib/tankHealthCheck";

/**
 * Jun 2026 — out-of-region completion section.
 *
 * Replaces the appointment calendar (never renders alongside it) for
 * properties outside the direct Sunshine Coast / Noosa Health Check
 * region. Same customer fields, same questionnaire — an ENQUIRY, not a
 * booking, so nothing here implies a confirmed appointment.
 */
export function ReferralPanel({
  contact, onContact, address, onEditAddress, locked, submitting,
  onSubmit, error, unresolved,
}) {
  return (
    <section id="book" className="mt-9 md:mt-14 rounded-[16px] border p-[18px] md:p-7"
      style={{ borderColor: THC.border, backgroundColor: "#FFFFFF", boxShadow: THC.shadow }}
      data-testid="thc-referral-panel">
      <div className="lg:grid lg:grid-cols-[minmax(0,52%)_minmax(0,48%)] lg:gap-7">
        <div>
          <h2 className="font-extrabold uppercase leading-[1.05] text-[29px] md:text-[32px] xl:text-[35px]"
            style={{ color: THC.navy, fontFamily: "'Outfit',system-ui,sans-serif" }}>
            SUBMIT YOUR WATER HEALTH ENQUIRY
          </h2>
          <div className="mt-3 rounded-[13px] border-2 p-4 md:p-5"
            style={{ borderColor: THC.yellow, backgroundColor: THC.yellowSoft }}
            data-testid="thc-out-of-region-message">
            <div className="flex items-start gap-2.5">
              <MapPin className="w-5 h-5 mt-0.5 shrink-0" style={{ color: THC.yellowInk }} aria-hidden="true" />
              <div>
                <h3 className="font-extrabold uppercase text-[17px] md:text-[19px]"
                  style={{ color: THC.yellowInk }}>
                  {unresolved
                    ? "We couldn't confirm your service area"
                    : "Outside our direct Health Check service area"}
                </h3>
                <p className="mt-1.5 text-[15.5px] md:text-[16px] leading-[1.5]" style={{ color: THC.body }}>
                  {unresolved
                    ? "We couldn't match your address to a service area. You can still submit your details and we'll check it for you — if your property sits outside our direct region we'll connect your enquiry with a Pristine Water Systems Partner who services your area."
                    : "Your property is outside our direct Sunshine Coast Health Check service area. You can still submit your details. We'll connect your enquiry with a Pristine Water Systems Partner who services your area. They may contact you directly to discuss your tank water system and suitable next steps."}
                </p>
              </div>
            </div>
          </div>
          <p className="mt-3 text-[14.5px] leading-[1.5]" style={{ color: THC.muted }}
            data-testid="thc-referral-no-appointment-note">
            This is an enquiry only — no appointment is being booked and no time is being held.
          </p>
        </div>

        <div className={`mt-5 lg:mt-0 ${locked ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="grid grid-cols-2 gap-2">
            <Field id="first_name" label="First name" value={contact.first_name} onChange={onContact} />
            <Field id="last_name" label="Last name" value={contact.last_name} onChange={onContact} />
            <Field id="email" label="Email" type="email" value={contact.email} onChange={onContact} className="col-span-2" />
            <Field id="mobile" label="Mobile" type="tel" value={contact.mobile} onChange={onContact} className="col-span-2" />
          </div>
          <div className="mt-2">
            <label htmlFor="thc-notes" className="block text-[12.5px] font-bold uppercase tracking-wide"
              style={{ color: THC.navy }}>
              Anything else we should pass on? (optional)
            </label>
            <textarea id="thc-notes" data-testid="thc-field-notes" rows={3}
              value={contact.notes || ""}
              onChange={(e) => onContact({ notes: e.target.value })}
              className="mt-1 w-full rounded-[8px] border px-3 py-2 text-[15.5px] outline-none focus:ring-2 focus:ring-[#09A9E6]"
              style={{ borderColor: THC.border, color: THC.body, backgroundColor: THC.faint }} />
          </div>
          <div className="mt-2 rounded-[8px] border px-3 py-2"
            style={{ borderColor: THC.border, backgroundColor: THC.faint }}
            data-testid="thc-referral-address-summary">
            <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: THC.muted }}>
              PROPERTY ADDRESS
            </div>
            <div className="text-[14px] font-bold leading-snug" style={{ color: THC.navy }}>
              {address.property_address || "Add your address in the PROPERTY ADDRESS field above"}
            </div>
            <button type="button" onClick={onEditAddress} data-testid="thc-referral-edit-address"
              className="mt-0.5 text-[12px] font-semibold underline" style={{ color: THC.blue }}>
              Change address
            </button>
          </div>
          {error && (
            <p className="mt-2 text-[14px] font-semibold" style={{ color: THC.red }}
              data-testid="thc-referral-error" role="alert">
              {error}
            </p>
          )}
          <button onClick={onSubmit} disabled={submitting || locked} data-testid="thc-submit-referral"
            className="mt-3 w-full h-[56px] rounded-[8px] text-white text-[16px] md:text-[17px] font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ backgroundColor: THC.blue }}>
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
              : <Send className="w-5 h-5" aria-hidden="true" />}
            SUBMIT MY WATER HEALTH ENQUIRY
          </button>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, value, onChange, type = "text", className = "" }) {
  return (
    <div className={className}>
      <label htmlFor={`thc-${id}`} className="block text-[12.5px] font-bold uppercase tracking-wide" style={{ color: THC.navy }}>
        {label}
      </label>
      <input id={`thc-${id}`} data-testid={`thc-field-${id}`} type={type} value={value || ""}
        onChange={(e) => onChange({ [id]: e.target.value })}
        className="mt-1 w-full h-[44px] rounded-[8px] border px-3 text-[15.5px] outline-none focus:ring-2 focus:ring-[#09A9E6]"
        style={{ borderColor: THC.border, color: THC.body, backgroundColor: THC.faint }} />
    </div>
  );
}
